// E2E crypto for the phone client: the initiator side of the Noise-IK-style
// handshake the Fabrika daemon implements in internal/relay/noise.go. That Go
// file is the protocol reference; this module is validated against its
// checked-in test vectors (test/vectors.json). The relay between us only ever
// sees the resulting ciphertext.
import { x25519 } from "@noble/curves/ed25519";
import { chacha20poly1305 } from "@noble/ciphers/chacha";
import { sha256 } from "@noble/hashes/sha2";
import { hkdf } from "@noble/hashes/hkdf";
import { randomBytes } from "@noble/hashes/utils";

const PROTO = "fabrika-relay/1";
const TAG_MSG1 = 0x01;
const TAG_MSG2 = 0x02;
const TAG_FRAME = 0x03;
const KEY_LEN = 32;
const AEAD_TAG = 16;
const NONCE_LEN = 12;
// Counter cap mirrors the daemon: hit it and the session re-handshakes.
const MAX_FRAME_CTR = 1n << 32n;

export interface Keypair {
  priv: Uint8Array;
  pub: Uint8Array;
}

export function generateKeypair(priv?: Uint8Array): Keypair {
  const p = priv ?? randomBytes(KEY_LEN);
  return { priv: p, pub: x25519.getPublicKey(p) };
}

export interface Msg2Payload {
  v: number;
  deviceId: string;
  project: string;
  paired: boolean;
}

export interface InitiatorState {
  phoneStatic: Keypair;
  eph: Keypair;
  h2: Uint8Array;
  encPayload: Uint8Array;
  dh1: Uint8Array;
  dh2: Uint8Array;
}

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

function deriveKey(ikm: Uint8Array, salt: Uint8Array | undefined, info: string): Uint8Array {
  return hkdf(sha256, ikm, salt, utf8(info), KEY_LEN);
}

const zeroNonce = (): Uint8Array => new Uint8Array(NONCE_LEN);

function ctrNonce(ctr: bigint): Uint8Array {
  const n = new Uint8Array(NONCE_LEN);
  new DataView(n.buffer).setBigUint64(4, ctr);
  return n;
}

// initiate builds Msg1 (ClientHello). payload is the already-serialized JSON
// {"v":1,"secret":…,"name":…} — secret/name only when pairing. ephPriv is for
// tests only (deterministic vectors).
export function initiate(
  daemonId: string,
  daemonPub: Uint8Array,
  phoneStatic: Keypair,
  payload: Uint8Array,
  ephPriv?: Uint8Array,
): { msg1: Uint8Array; state: InitiatorState } {
  const eph = generateKeypair(ephPriv);

  const h1 = sha256(concat(utf8(PROTO), utf8(daemonId), eph.pub));
  const dh1 = x25519.getSharedSecret(eph.priv, daemonPub); // es
  const k1 = deriveKey(dh1, h1, "m1.static");
  const encPS = chacha20poly1305(k1, zeroNonce(), h1).encrypt(phoneStatic.pub);

  const h2 = sha256(concat(h1, encPS));
  const dh2 = x25519.getSharedSecret(phoneStatic.priv, daemonPub); // ss
  const k2 = deriveKey(concat(dh1, dh2), h2, "m1.payload");
  const encPayload = chacha20poly1305(k2, zeroNonce(), h2).encrypt(payload);

  const msg1 = concat(new Uint8Array([TAG_MSG1]), eph.pub, encPS, encPayload);
  return { msg1, state: { phoneStatic, eph, h2, encPayload, dh1, dh2 } };
}

// finish processes Msg2 (ServerHello); a successful decrypt authenticates the
// daemon and yields the transport session.
export function finish(st: InitiatorState, msg2: Uint8Array): { session: Session; payload: Msg2Payload } {
  if (msg2.length < 1 + KEY_LEN + AEAD_TAG || msg2[0] !== TAG_MSG2) {
    throw new Error("relay: bad ServerHello");
  }
  const daemonEph = msg2.slice(1, 1 + KEY_LEN);
  const encM2 = msg2.slice(1 + KEY_LEN);

  const h3 = sha256(concat(st.h2, st.encPayload, daemonEph));
  const dh3 = x25519.getSharedSecret(st.eph.priv, daemonEph); // ee
  const dh4 = x25519.getSharedSecret(st.phoneStatic.priv, daemonEph); // se

  const ck = deriveKey(concat(st.dh1, st.dh2, dh3, dh4), h3, "session");
  const kc2d = deriveKey(ck, undefined, "c2d");
  const kd2c = deriveKey(ck, undefined, "d2c");

  const m2 = chacha20poly1305(kd2c, zeroNonce(), h3).decrypt(encM2);
  const payload = JSON.parse(new TextDecoder().decode(m2)) as Msg2Payload;
  return { session: new Session(kc2d, kd2c), payload };
}

// Session encrypts transport frames. Single-writer per direction; counters
// are strictly increasing (replay protection — order is guaranteed by the
// WebSocket hops).
export class Session {
  private sendCtr = 0n;
  private recvCtr = 0n;

  constructor(
    private sendKey: Uint8Array, // phone → daemon
    private recvKey: Uint8Array, // daemon → phone
  ) {}

  seal(plaintext: Uint8Array): Uint8Array {
    this.sendCtr++;
    if (this.sendCtr >= MAX_FRAME_CTR) throw new Error("relay: frame counter exhausted");
    const header = new Uint8Array(9);
    header[0] = TAG_FRAME;
    new DataView(header.buffer).setBigUint64(1, this.sendCtr);
    const ct = chacha20poly1305(this.sendKey, ctrNonce(this.sendCtr)).encrypt(plaintext);
    return concat(header, ct);
  }

  open(frame: Uint8Array): Uint8Array {
    if (frame.length < 9 + AEAD_TAG || frame[0] !== TAG_FRAME) {
      throw new Error("relay: bad frame");
    }
    const ctr = new DataView(frame.buffer, frame.byteOffset).getBigUint64(1);
    if (ctr <= this.recvCtr) throw new Error("relay: frame replayed or reordered");
    const pt = chacha20poly1305(this.recvKey, ctrNonce(ctr)).decrypt(frame.slice(9));
    this.recvCtr = ctr;
    return pt;
  }
}

// --- byte-encoding helpers shared by the client ---

export function b64decode(s: string): Uint8Array {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function b64encode(b: Uint8Array): string {
  let bin = "";
  for (const x of b) bin += String.fromCharCode(x);
  return btoa(bin);
}

export function b64urlDecode(s: string): Uint8Array {
  return b64decode(s);
}

export function hexDecode(s: string): Uint8Array {
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function hexEncode(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}
